import axios from 'axios';
import React, { useEffect, useState } from 'react';
import './TestAutocomplete.css';

const API_BASE_URL = 'https://apiv2.labridge.in';

const getSuggestions = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.testData)) return data.testData;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

function TestAutocomplete({ scope, clientId, labCodes = [], onSelect, disabled = false, label = 'Search test' }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const requiresClient = scope === 'client-wise';
  const inputDisabled = disabled || (requiresClient && !clientId);

  useEffect(() => {
    if (query.trim().length < 2 || inputDisabled) {
      setSuggestions([]);
      setHasSearched(false);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      setHasSearched(false);
      try {
        const response = requiresClient
          ? await axios.get(`${API_BASE_URL}/admin/testdetails`, { params: { term: query.trim(), lab: clientId } })
          : await axios.get(`${API_BASE_URL}/apiforsubmission/test/suggestions`, {
              params: {
                testName: query.trim(),
                ...(labCodes.length > 0
                  ? { labCode: labCodes[0], labCodes: labCodes.join(',') }
                  : {}),
              },
            });
        if (active) {
          const results = getSuggestions(response.data);
          const selectedCenterResults = !requiresClient && labCodes.length > 0
            ? results.filter((test) => labCodes.includes(test.labCode))
            : results;
          setSuggestions(selectedCenterResults);
        }
      } catch (error) {
        if (active) setSuggestions([]);
      } finally {
        if (active) {
          setIsLoading(false);
          setHasSearched(true);
        }
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [query, clientId, inputDisabled, labCodes, requiresClient]);

  const selectTest = (test) => {
    onSelect(test);
    setQuery(`${test.testName || 'Test'} (${test.testId || '-'})`);
    setSuggestions([]);
    setHasSearched(false);
  };

  return (
    <div className="test-autocomplete">
      <label className="form-label">{label}</label>
      <input
        type="text"
        className="form-control"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={requiresClient && !clientId ? 'Select client first' : 'Type at least 2 characters'}
        disabled={inputDisabled}
      />
      {requiresClient && !clientId && <div className="form-text">Select client first to search assigned tests.</div>}
      {isLoading && <div className="form-text">Searching tests…</div>}
      {!isLoading && hasSearched && suggestions.length === 0 && <div className="test-autocomplete-empty">No matching test found</div>}
      {suggestions.length > 0 && (
        <div className="test-autocomplete-results" role="listbox" aria-label="Test search results">
          {suggestions.map((test, index) => (
            <button type="button" className="test-autocomplete-option" key={test._id || `${test.testId}-${index}`} onClick={() => selectTest(test)}>
              <span className="test-autocomplete-name">{test.testName || 'Unnamed test'}</span>
              <span className="test-autocomplete-id">ID: {test.testId || '-'}</span>
              <span className="test-autocomplete-meta">
                {test.labCode && <>{test.labCode} </>}
                {String(test.isProfile) === '1' && 'Profile '}
                {String(test.isPackage) === '1' && 'Package '}
                {String(test.isTest) === '1' && 'Test'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default TestAutocomplete;
